import { identity } from "fp-ts/lib/function";
import { pipe } from "fp-ts/lib/pipeable";
import { getBreadcrumbSchema } from "lib/schema";
import Head from "next/head";
import { useRouter } from "next/router";
import { defineMessage, useIntl } from "react-intl";
import { useFigureScreenshotUrl } from "../figures";
import { foldLocaleL, type Locale } from "../locales";
import { io, O } from "../prelude";
import { isNoIndexRoute } from "../routes";

import graphImgSrcImg from "./graph-images/unesco-education-progress.jpg";

function publicUrl(path: string) {
  return `${process.env.SITE_URL}${path}`;
}

function getCanonicalURL(path: string) {
  // remove queries and in-page id link
  const _pathSliceLength = Math.min.apply(Math, [
    path.indexOf("?") > 0 ? path.indexOf("?") : path.length,
    path.indexOf("#") > 0 ? path.indexOf("#") : path.length,
  ]);
  return `${process.env.SITE_URL}${path.substring(0, _pathSliceLength)}`;
}

const graphImgSrc = foldLocaleL<string>({
  ar: () => graphImgSrcImg.src,
  de: () => graphImgSrcImg.src,
  en: () => graphImgSrcImg.src,
  es: () => graphImgSrcImg.src,
  fr: () => graphImgSrcImg.src,
  ru: () => graphImgSrcImg.src,
  zh: () => graphImgSrcImg.src,
});

const PageType = io.union([io.literal("website"), io.literal("article")]);
type PageType = io.TypeOf<typeof PageType>;

export function HeadMetaDynamic({
  locale,
  title,
  description,
  pageType,
}: {
  locale: Locale;
  title: string;
  description: string;
  pageType: PageType;
}) {
  const router = useRouter();
  const intl = useIntl();

  const figureId = typeof router.query.figure === "string" ? router.query.figure : "";

  const screenshotUrl = pipe(
    useFigureScreenshotUrl(figureId),
    O.fold(() => publicUrl(graphImgSrc(locale)), identity),
  );

  const siteTitle = intl.formatMessage(
    defineMessage({ id: "meta.title", defaultMessage: "GEM Report Education Progress" }),
  );
  const headTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  // @todo
  // og and twitter title normally don't contain a suffix site title
  // but it could be. Decide this after checking card preview
  const pageTitle = title || siteTitle;
  const pageDesp =
    description ||
    intl.formatMessage(
      defineMessage({
        id: "meta.description",
        defaultMessage: "An interactive site telling the story behind education data from around the world.",
      }),
    );
  const pageCanonicalURL = getCanonicalURL(router.asPath);
  const noIndex = isNoIndexRoute(router.pathname);

  // Currently we only have subpages under `/articles`
  const isArticle = router.pathname.startsWith("/articles");
  const breadcrumb = isArticle
    ? [
        {
          position: 1,
          name: siteTitle,
          item: `${process.env.SITE_URL}/${locale}`,
        },
        {
          position: 2,
          name: headTitle,
          item: pageCanonicalURL,
        },
      ]
    : null;

  return (
    <Head>
      {/* Prevent robots from crawling the page */}
      {noIndex && <meta name="robots" content="noindex" />}

      {/* Essential META Tags */}
      <title>{headTitle}</title>
      <meta name="description" content={pageDesp} />

      {/* Solve GoogleBot "Duplicate without user-selected canonical" issue */}
      <link rel="canonical" href={pageCanonicalURL} />

      {/* OpenGraph tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesp} />
      <meta property="og:type" content={pageType} />
      <meta property="og:url" content={pageCanonicalURL} />
      <meta property="og:image" content={screenshotUrl} />
      <meta property="og:site_name" content={siteTitle} />

      {/* Twitter Card data */}
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesp} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@GEMReport" />
      <meta name="twitter:creator" content="@GEMReport" />
      <meta name="twitter:image" content={screenshotUrl} />

      {/* Structured data */}
      {breadcrumb && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getBreadcrumbSchema(breadcrumb),
          }}
        />
      )}
    </Head>
  );
}
